import Parse from "parse"
import { push } from "react-router-redux"
import { cloneDeep } from "lodash"
import { v4 as uuidv4 } from "uuid"

import { loadMachineTypes } from "../Machines/Machines"
import { loadKitchenAreaBySiteId } from "../Site/sites"
import { loadSupplierItemsToReplace } from "../Supplier/supplierItems"
import { loadTransformationModes } from "../TransformationModes/TransformationModes"
import { actionWithLoader, getReplacingPath, onEnter } from "../Utils/utils"
import { createOrUpdateProductionStep, parseProductionStepsToSave } from "../../utils/recipes"
import { getReusableProductionStep, getReusableProductionSteps, getReusableProductionStepsPipeline } from "../../parseManager/recipe/parseReusableProductionStepsManager"
import { getServerUrl, replacePropertyValuesFromMap } from "../../utils"
import { axiosCall } from "../../utils/axiosUtils"
import { openAppMessageSnackBar } from "../Utils/app"
import { getReusableProductionStepsPipelineSelector, getReusableStepsSupplierItemsSelector } from "../../reducers/Steps/steps"
import { getTransformationModesSelector } from "../../reducers/TransformationModes/transformationModes"
import {
  getIngredientsWithoutWeight,
  getMissingWeightMessage,
  getRebuildSuccessMessage,
  getTooManyRecipesBlockedMessage,
  getTooManyRecipesNotUpdatedMessage,
  getTransformationModeConflictsMessage,
  hasRecipesToReview,
  REBUILD_ERROR_MESSAGE
} from "../../utils/reusableStepRebuild"

const ReusableProductionStep = Parse.Object.extend("ReusableProductionStep")

export const loadProductionStepsRelatedData = () => {
  return actionWithLoader(async (dispatch) => {
    dispatch(loadMachineTypes(null, []))
    dispatch(loadKitchenAreaBySiteId())
    dispatch(loadSupplierItemsToReplace(true))
    dispatch(loadTransformationModes(true))
  })
}

export const REUSABLE_STEP_URL = "/settings/resources/reusableSteps/"
export const REUSABLE_STEP_VIEW_URL = REUSABLE_STEP_URL + "view/"

export const createOrUpdateReusableProductionStep = (values) => {
  return actionWithLoader(async (dispatch, getState) => {
    // KFC-565: an edited reusable step with an ingredient at 0 g would update its recipes with it
    if (values.id) {
      const ingredientsWithoutWeight = getIngredientsWithoutWeight(values.productionSteps)
      if (ingredientsWithoutWeight.length) {
        dispatch(openAppMessageSnackBar(getMissingWeightMessage(ingredientsWithoutWeight), "error", 15000))
        return
      }
    }

    let reusableProductionStep = await getReusableProductionStep(values.id, false)
    if (!reusableProductionStep) {
      reusableProductionStep = new ReusableProductionStep()
    }

    reusableProductionStep.set("name", values.name)
    const formattedProductionStepsValues = parseProductionStepsToSave(values.productionSteps)

    // KFC-565: an edition making a transformation mode of a recipe invalid, or calling for too
    // many recipes to update, is not saved
    if (values.id) {
      const { transformationModeConflicts, tooManyRecipes } = await checkReusableStepUpdate(values.id, formattedProductionStepsValues)
      if (tooManyRecipes) {
        dispatch(openAppMessageSnackBar(getTooManyRecipesBlockedMessage(tooManyRecipes), "error", 15000))
        return
      }
      if (transformationModeConflicts.length) {
        dispatch(openAppMessageSnackBar(getTransformationModeConflictsMessage(transformationModeConflicts, getTransformationModesSelector(getState())), "error", 15000))
        return
      }
    }

    const productionSteps = await createOrUpdateProductionStep(formattedProductionStepsValues)
    reusableProductionStep.set("productionSteps", productionSteps)

    await reusableProductionStep.save(null, { context: { updateProductionSteps: true } })
    // KFC-565: the result of the recipes update is shown on the reusable step itself
    dispatch(showReusableProductionStepView(reusableProductionStep.id))

    // KFC-565: update the recipes calling this reusable step
    await dispatch(rebuildRecipesAfterReusableStepUpdate(reusableProductionStep.id))
  })
}

/**
 * KFC-565: asks the server whether the production steps about to be saved keep valid the
 * transformation modes the recipes apply to the reusable step ingredients, and whether the
 * recipes are not too many to be updated.
 * When the check itself fails, the save goes on: the recipes update then lists the recipes in
 * conflict for a manual update instead of changing them.
 * @param {String} reusableProductionStepId
 * @param {Array<Object>} productionSteps
 * @returns {Promise<{transformationModeConflicts: Array<Object>, tooManyRecipes: ?{recipesCount: Number, limit: Number}}>}
 */
const checkReusableStepUpdate = async (reusableProductionStepId, productionSteps) => {
  try {
    const url = `${getServerUrl()}/recipes/checkReusableStepUpdate`
    const { data } = await axiosCall("post", url, { reusableProductionStepId, productionSteps }, { "Content-Type": "application/json" })
    return {
      transformationModeConflicts: (data && data.transformationModeConflicts) || [],
      tooManyRecipes: (data && data.tooManyRecipes) || null
    }
  } catch (error) {
    return { transformationModeConflicts: [], tooManyRecipes: null }
  }
}

/**
 * KFC-565: rebuilds on the server the recipes calling the reusable step, then shows the result:
 * a success snackbar, and the recipes the user has to update himself (Division des Sauces) or
 * that could not be written.
 * @param {String} reusableProductionStepId
 */
export const rebuildRecipesAfterReusableStepUpdate = (reusableProductionStepId) => {
  // the progress dialog replaces the global loader, which another loading can end too early
  return async (dispatch) => {
    dispatch({ type: "REUSABLE_STEP_REBUILD_STARTED" })
    try {
      const url = `${getServerUrl()}/recipes/rebuildAfterReusableStepUpdate`
      const { data } = await axiosCall("post", url, { reusableProductionStepId }, { "Content-Type": "application/json" })

      if (data && data.tooManyRecipes) {
        dispatch(openAppMessageSnackBar(getTooManyRecipesNotUpdatedMessage(data.tooManyRecipes), "error", 15000))
        return
      }

      dispatch(openAppMessageSnackBar(getRebuildSuccessMessage(data), "success"))

      if (hasRecipesToReview(data)) {
        dispatch({
          type: "REUSABLE_STEP_REBUILD_REPORT_LOADED",
          rebuildReport: {
            reusableProductionStepId,
            recipesToUpdateManually: data.recipesToUpdateManually || [],
            recipesFailed: data.recipesFailed || []
          }
        })
      }
    } catch (error) {
      dispatch(openAppMessageSnackBar(REBUILD_ERROR_MESSAGE, "error"))
    } finally {
      dispatch({ type: "REUSABLE_STEP_REBUILD_ENDED" })
    }
  }
}

export const closeReusableStepRebuildReport = () => ({ type: "REUSABLE_STEP_REBUILD_REPORT_CLOSED" })

// @INFO: pipeline param to load lighter but faster version
/**
 * 
 * @param {*} mode default or pipeline or list 
 * @returns 
 */
export const loadReusableProductionSteps = (mode = "default") => {
  return actionWithLoader(async (dispatch) => {
    const [reusableProductionSteps, supplierItems] = await Promise.all([
      mode === "default" ? getReusableProductionSteps() : getReusableProductionStepsPipeline(mode === "list"),
      Parse.Cloud.run("getSupplierItemsInReusableSteps")
    ])

    dispatch({
      type: mode === "default" ? "REUSABLE_STEPS_LOADED" : mode === "list" ? "REUSABLE_STEPS_LIST_LOADED" : "REUSABLE_STEPS_PIPELINE_LOADED",
      reusableProductionSteps,
      supplierItems
    })
    return { reusableProductionSteps, supplierItems }
  })
}

export const deleteReusableStep = (id) => {
  return actionWithLoader(async (dispatch, getState) => {
    const state = getState()
    const prevReusableSteps = getReusableProductionStepsPipelineSelector(state)
    const prevSupplierItems = getReusableStepsSupplierItemsSelector(state)
    const reusableProductionStep = await getReusableProductionStep(id, false)

    if (!reusableProductionStep) {
        throw new Error("Étape réutilisable introuvable")
    }

    reusableProductionStep.set("deleted", true)
    await reusableProductionStep.save(null, { context: { disableAfterSave: true } })

    const newReusableSteps = prevReusableSteps.filter(step => step.objectId !== reusableProductionStep.id)

    dispatch({
      type: "REUSABLE_STEPS_PIPELINE_LOADED",
      reusableProductionSteps: newReusableSteps,
      supplierItems: prevSupplierItems
    })
  })
}

export const loadReusableProductionStepEdition = (params) => {
  return actionWithLoader(async (dispatch) => {
    const reusableProductionStep = await getReusableProductionStep(params.id)

    dispatch({
      type: "REUSABLE_STEP_LOADED",
      reusableProductionStep,
      isEdition: true
    })

    dispatch(loadTransformationModes(true))
    // load data for form
    dispatch(loadProductionStepsRelatedData())
  })
}

export const sectionToReusableProductionStep = (section) => {
  return actionWithLoader(async (dispatch) => {
    const reusableProductionStep = new ReusableProductionStep()
    reusableProductionStep.set("name", section.name)
    const indexMap = new Map()
    const newProductionSteps = cloneDeep(section.productionSteps).filter(s => !s.reusable).map((step) => {
      delete step.step.objectId
      const oldStepIndex = step.step.index
      step.step.index = uuidv4()
      indexMap.set(oldStepIndex, step.step.index)
      step.step.stepComponents = (step.step.stepComponents || []).map((sc) => {
        const scIndex = uuidv4()
        indexMap.set(sc.index, scIndex)
        return {
          ...sc,
          index: scIndex,
        }
      })
      return replacePropertyValuesFromMap(step, indexMap)
    })
    const productionSteps = await createOrUpdateProductionStep(newProductionSteps, false, true)
    reusableProductionStep.set("productionSteps", productionSteps.map((step) => step.step))

    if (section.productionSteps.length > 0) {
      const newName = section.productionSteps[section.productionSteps.length - 1].step.name.toUpperCase()
      if (newName) reusableProductionStep.set("name", newName.toUpperCase())
    }
    const newPs = await reusableProductionStep.save()
    const allReusables = await dispatch(loadReusableProductionSteps())
    return { newPs, ...allReusables }
  })
}

/**
 * duplicate reusable production step and its production steps to create a new one
 * the redirect to the new reusable production step view
 * @param {*} id 
 * @returns 
 */
export const duplicateReusableProductionStep = (id, shouldAddOption) => {
  return actionWithLoader(async (dispatch) => {
    const clonedReusableProductionStep = await Parse.Cloud.run("duplicateReusableProductionStep", { id, shouldAddOption })

    dispatch(showReusableProductionStepView(clonedReusableProductionStep.id))
  })
}

export const exportReusableStepUsage = (id) => {
  return actionWithLoader(async (dispatch) => {
    try {
      const url = `${getServerUrl()}/steps/${id}/extractRecipes`
      const result = await axiosCall("post", url, {}, { "Content-Type": "application/json" }, "blob")
      const csvUrl = window.URL.createObjectURL(new Blob([result.data]))
      const link = document.createElement("a")
      const fileName = `Export-${id}-FT.csv`

      link.href = csvUrl
      link.setAttribute("download", fileName)
      document.body.appendChild(link)
      link.click()
      link.remove()

      dispatch(openAppMessageSnackBar(`La liste des produits internes contentant l'étape réutilisable ${id} a été exportée`))
    } catch (error) {
      dispatch(openAppMessageSnackBar(error.message, "error"))
    }
  })
}

export const loadReusableProductionStepView = (params) => {
  return actionWithLoader(async (dispatch) => {
    const reusableProductionStep = await getReusableProductionStep(params.id)
    dispatch({
      type: "REUSABLE_STEP_LOADED",
      reusableProductionStep,
      isEdition: false
    })

    // load data for form
    dispatch(loadProductionStepsRelatedData())
  })
}

export const fixRecipesReusableStepsWithSameIndex = (values) => {
  return actionWithLoader(async (dispatch) => {
    await Parse.Cloud.run("fixRecipesReusableStepsWithSameIndex", values)

    dispatch(openAppMessageSnackBar("Processus de correction des étapes réutilisables lancé", "success"))
  })
}

export const resetProductionStepView = () => {
  return actionWithLoader(async (dispatch) => {
    dispatch({
      type: "REUSABLE_STEP_LOADED",
      reusableProductionStep: null,
      isNew: true
    })

    // load data for form
    dispatch(loadProductionStepsRelatedData())
  })
}


export const onEnterReusableProductionStepCreation = (store) => {
  return onEnter({
    store,
    actionThunk: resetProductionStepView,
    getReplacingPath: getReplacingPath({ needUser: true })
  })
}

export const onEnterReusableProductionSteps = (store) => {
  return onEnter({
    store,
    actionThunk: () => loadReusableProductionSteps("pipeline")
  })
}

export function onEnterReusableProductionStepEdition(store) {
  return onEnter({
    store,
    actionThunk: loadReusableProductionStepEdition,
    getReplacingPath: getReplacingPath({ needUser: true })
  })
}
export function onEnterReusableProductionStepView(store) {
  return onEnter({
    store,
    actionThunk: loadReusableProductionStepView,
    getReplacingPath: getReplacingPath({ needUser: true })
  })
}

export const showReusableProductionStepCreation = () => {
  return push("/settings/resources/reusableSteps/add")
}

export const showReusableProductionSteps = () => {
  return push("/settings/resources/reusableSteps")
}

export const showReusableProductionStepEdition = (id) => {
  return push("/settings/resources/reusableSteps/edit/" + id)
}

export const showReusableProductionStepView = (id) => {
  return push(REUSABLE_STEP_VIEW_URL + id)
}